import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar";
import Footer from "../components/sub-comp/MainFooter";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import cmsApi from "../services/cmsApi";
import forenzyImage from "../assets/forenzy.png";

const FALLBACK_SERVICES = [
  { id: 'cyber', category: 'network', title: 'Cyber Security Services', description: 'We provide online security services through our partners. Explore our cyber security services to fortify your digital defences. Detect, mitigate, and prevent threats.', image: '', linkUrl: 'https://forenzy.net/services/', linkText: 'Learn More', iconType: 'shield' },
  { id: 'media', category: 'application', title: 'JR Media Productions', description: 'Professional video production, photography, and multimedia services. From corporate videos to event coverage, we bring your vision to life with high-quality production.', image: '', linkUrl: '/services/jr-media-productions', linkText: 'Learn More', iconType: 'video' },
  { id: 'data', category: 'cloud', title: 'Data Analysis Services', description: 'Transform your data into actionable insights. Our data analysis services help you make informed decisions through statistical analysis, visualization, and reporting.', image: '', linkUrl: '/services/data-analysis', linkText: 'Learn More', iconType: 'chart' },
];

const FALLBACK_CATEGORIES = [
  { id: 'all', label: 'All Services' },
  { id: 'network', label: 'Network Security' },
  { id: 'application', label: 'Application Security' },
  { id: 'cloud', label: 'Cloud Security' },
];

const safeParse = (json, fallback) => {
  if (!json) return fallback;
  try { return JSON.parse(json); } catch { return fallback; }
};

const Services = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [heroData, setHeroData] = useState({ title: 'Services', subtitle: '' });
  const [servicesList, setServicesList] = useState(FALLBACK_SERVICES);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [ctaData, setCtaData] = useState({ title: 'Ready to Get Started?', subtitle: 'Contact us today for a consultation about our services', ctaText: 'Get Started', ctaLink: '/contact' });
  const [visibleSectionKeys, setVisibleSectionKeys] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const result = await cmsApi.getPageComponents('services');
        if (result?.success && result.components?.length) {
          const keys = [];
          const map = {};
          result.components.forEach((c) => {
            map[c.componentName] = c;
            keys.push(c.componentName);
          });
          setVisibleSectionKeys(keys);
          if (map.hero) setHeroData({ title: map.hero.title || 'Services', subtitle: map.hero.subtitle || '' });
          if (map['services-list']) {
            const items = safeParse(map['services-list'].content, FALLBACK_SERVICES);
            setServicesList(items);
          }
          if (map.categories) setCategories(safeParse(map.categories.content, FALLBACK_CATEGORIES));
          if (map.cta) setCtaData({ title: map.cta.title || 'Ready to Get Started?', subtitle: map.cta.subtitle || '', ctaText: map.cta.ctaText || 'Get Started', ctaLink: map.cta.ctaLink || '/contact' });
        }
      } catch (e) {
        console.error('Failed to fetch services CMS data:', e);
      }
    };
    loadData();
  }, []);

  const showCmsSection = (key) =>
    visibleSectionKeys == null || visibleSectionKeys.includes(key);

  const isCardVisible = (id) => {
    if (activeCategory === 'all') return true;
    const svc = servicesList.find((s) => s.id === id);
    return svc ? svc.category === activeCategory : true;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="mid-desktop-page">
      
      {/* Hero Section */}
      {showCmsSection('hero') && (
      <section className="relative py-20 overflow-hidden">
        {/* Gear Icons Background Decorations */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Multiple gear icons scattered around */}
          <div className="absolute left-0 top-1/4 w-12 h-12 text-custom-pink opacity-20">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
              <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
            </svg>
          </div>
          
          <div className="absolute right-0 top-1/3 w-16 h-16 text-gold opacity-15">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
              <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
            </svg>
          </div>

          <div className="absolute left-1/4 top-1/2 w-10 h-10 text-custom-pink opacity-25">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
              <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12-0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
            </svg>
          </div>
          
          <div className="absolute right-1/4 top-1/4 w-8 h-8 text-gold opacity-20">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
              <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
            </svg>
          </div>

          <div className="absolute left-1/3 bottom-1/4 w-14 h-14 text-custom-pink opacity-15">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
              <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
            </svg>
          </div>

          <div className="absolute right-1/3 bottom-1/3 w-6 h-6 text-gold opacity-25">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
              <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
            </svg>
          </div>

          <div className="absolute left-1/2 top-1/6 w-9 h-9 text-custom-pink opacity-20">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
              <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
            </svg>
          </div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-6xl font-bold text-gray-800 mb-6">{heroData.title}</h1>
          
          {/* Breadcrumb */}
          <nav className="flex justify-center mb-8">
            <ol className="flex items-center space-x-2 text-gray-600">
              <li>
                <a href="/" className="hover:text-custom-pink transition-colors">Home</a>
              </li>
              <li className="text-gray-400">/</li>
              <li className="text-custom-pink font-medium">Services</li>
            </ol>
          </nav>
        </div>
      </section>
      )}

      {/* Our Services Section */}
      {(showCmsSection('services-list') || showCmsSection('categories')) && (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Take a Look at Our <span className="text-custom-pink">Top Services</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
Explore our expert solutions designed to meet your unique needs and deliver exceptional results.
            </p>
          </div>

          {/* Service Categories */}
          {showCmsSection('categories') && (
          <div className="flex justify-center mb-12">
            <div className="bg-white rounded-full p-2 shadow-lg w-full max-w-4xl">
              <div className="flex flex-wrap justify-center gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`px-4 py-2 rounded-full font-medium text-sm sm:px-6 sm:text-base whitespace-nowrap ${
                      activeCategory === cat.id
                        ? 'bg-custom-pink text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={() => setActiveCategory(cat.id)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          )}

          {/* Service Cards Grid */}
          {showCmsSection('services-list') && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesList.filter(svc => isCardVisible(svc.id)).map((svc, idx) => {
              const isExternal = svc.linkUrl && svc.linkUrl.startsWith('http');
              const colors = idx % 2 === 0 ? { icon: 'text-custom-pink', bg: 'bg-custom-pink/10', link: 'text-custom-pink hover:text-purple-600' } : { icon: 'text-gold', bg: 'bg-gold/10', link: 'text-gold hover:text-yellow-600' };
              return (
                <div key={svc.id} className="bg-[#eff6ff] rounded-xl shadow-lg border border-gray-200">
                  <div className="p-6">
                    <div className={`w-16 h-16 ${colors.bg} rounded-xl flex items-center justify-center mb-4`}>
                      <svg className={`w-8 h-8 ${colors.icon}`} fill="currentColor" viewBox="0 0 24 24">
                        {svc.iconType === 'shield' && <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>}
                        {svc.iconType === 'video' && <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zm-10-7v6l5-3-5-3z"/>}
                        {svc.iconType === 'chart' && <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>}
                        {!['shield','video','chart'].includes(svc.iconType) && <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>}
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">{svc.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{svc.description}</p>
                    {svc.image ? (
                      <div className="mb-4">
                        <img src={svc.image} alt={svc.title} className="w-full h-32 object-cover rounded-lg" />
                      </div>
                    ) : svc.id === 'cyber' ? (
                      <div className="mb-4">
                        <img src={forenzyImage} alt={svc.title} className="w-full h-32 object-cover rounded-lg" />
                      </div>
                    ) : (
                      <div className="mb-4 bg-gray-200 rounded-lg h-32 flex items-center justify-center">
                        <svg className={`w-12 h-12 text-gray-500`} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                        </svg>
                      </div>
                    )}
                    {isExternal ? (
                      <a href={svc.linkUrl} target="_blank" rel="noopener noreferrer" className={`${colors.link} font-medium transition-colors inline-flex items-center`}>
                        {svc.linkText || 'Learn More'}
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </a>
                    ) : (
                      <Link to={svc.linkUrl || '/contact'} className={`${colors.link} font-medium transition-colors inline-flex items-center`}>
                        {svc.linkText || 'Learn More'}
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      </section>
      )}

      {/* Call to Action */}
      {showCmsSection('cta') && (
      <section className="bg-custom-pink text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">{ctaData.title}</h2>
          <p className="text-lg mb-8 opacity-90">{ctaData.subtitle}</p>
          <Link 
            to={ctaData.ctaLink} 
            className="inline-block bg-white text-custom-pink px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            {ctaData.ctaText}
          </Link>
        </div>
      </section>
      )}

      </div>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default Services;